/**
 * entry-frame.tsx — every way into the game is one window of slate, drawn
 * here once: the plane mark and the game's name along the top (the mark alone
 * on the landing, whose headline is the name), the language control at the
 * top right where the link names no language, the page header's kicker —
 * the session, on every way in but the landing — headline and one sentence of
 * meaning beside the way in's card, and along the foot the version line and
 * the copyright.
 *
 * A way in has one headline whatever its state; the card carries the state —
 * who is joining, how far the join has come, what refused it, the one gold
 * action. Under 1100px the card drops under the words, and a window shorter
 * than the card scrolls.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { linkLanguage } from '../../i18n/config';
import { PageHeader } from '../ui/page-header';
import { SegmentedControl } from '../ui/segmented-control';
import { PlaneMark } from '../common/plane-mark';
import { useLanguageChoice } from '../common/use-language-choice';
import { Copyright, VersionLine } from '../common/version-line';
import styles from './entry-frame.module.css';

interface EntryFrameProps {
  /** The page's link: the language control shows only where it names no language. */
  search: string;
  /** The headline: the way in's own, the same in every state. */
  title: string;
  /** What follows the kicker's lead, in the caption ink: the world, once the game knows it. */
  kicker?: readonly string[];
  /** One sentence under the headline. */
  meaning?: ReactNode;
  /**
   * The plane mark and the game's name, or the mark alone on the landing,
   * whose headline is the name — and which, opening no session, has no
   * kicker.
   */
  brand?: 'name' | 'mark';
  /** The way in's card. */
  children: ReactNode;
}

export function EntryFrame({ search, title, kicker, meaning, brand = 'name', children }: EntryFrameProps) {
  const { t } = useTranslation();
  const landing = brand === 'mark';
  return (
    <div className={styles.frame}>
      <header className={styles.top}>
        <span className={styles.brand}>
          <PlaneMark />
          {!landing && t('login.title')}
        </span>
        {linkLanguage(search) === null && (
          <span className={styles.language}>
            <EntryLanguage />
          </span>
        )}
      </header>
      <main className={styles.middle}>
        <div className={styles.grid}>
          <PageHeader kickerLead={landing ? undefined : t('entry.session')} kicker={kicker} title={title} meaning={meaning} />
          <div className={styles.side}>{children}</div>
        </div>
      </main>
      <footer className={styles.foot}>
        <VersionLine />
        <Copyright />
      </footer>
    </div>
  );
}

/**
 * The language, on a way in whose link names none: the languages the client
 * ships, each named in its own words, the one in use the raised plate
 * (useLanguageChoice). The choice is kept in this browser, as Settings keeps
 * it, until a link names another.
 */
function EntryLanguage() {
  return <SegmentedControl {...useLanguageChoice()} />;
}
